import { useRouter } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Plus,
  Search,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCommitments } from "@/hooks/use-commitments";
import { useBillingPeriods, useCreateSubPayApp, useSubPayApps } from "@/hooks/use-pay-applications";
import { useProjects } from "@/hooks/use-projects";

const STATUSES = [
  "All",
  "Draft",
  "Submitted",
  "Under Review",
  "Approved",
  "Rejected",
  "Returned",
  "Paid",
] as const;

type SortCol =
  | "payAppNumber"
  | "projectName"
  | "vendor"
  | "commitmentNumber"
  | "billingPeriodEnd"
  | "currentPaymentDue"
  | "createdAt"
  | "status";

function statusToFilter(label: string): string | undefined {
  if (label === "All") return undefined;
  if (label === "Returned") return "returned_for_revision";
  return label.toLowerCase().replace(/ /g, "_");
}

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "approved":
    case "paid":
      return "success";
    case "draft":
    case "submitted":
      return "warning";
    case "under_review":
      return "secondary";
    case "rejected":
    case "returned_for_revision":
      return "destructive";
    default:
      return "default";
  }
}

function formatStatusLabel(status: string | null): string {
  if (!status) return "--";
  if (status === "returned_for_revision") return "Returned";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

interface SubPayAppsListProps {
  /** When set, scopes the list to a single project and hides project filters/columns. */
  projectId?: string;
  /** Heading text. Defaults to "Subcontract Pay Applications". Pass empty string to hide. */
  title?: string;
}

export function SubPayAppsList({
  projectId,
  title = "Subcontract Pay Applications",
}: SubPayAppsListProps) {
  const router = useRouter();
  const scoped = Boolean(projectId);
  const { data, isLoading, isError, error, refetch } = useSubPayApps(
    projectId ? { projectId } : undefined,
  );
  const [activeStatus, setActiveStatus] = useState("All");
  const [filterProject, setFilterProject] = useState("__all__");
  const [filterVendor, setFilterVendor] = useState("__all__");
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId ?? "");
  const [commitmentId, setCommitmentId] = useState("");
  const [billingPeriodId, setBillingPeriodId] = useState("");
  const [sortCol, setSortCol] = useState<SortCol>("payAppNumber");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const { data: projectsList } = useProjects();
  const { data: commitmentsList } = useCommitments(
    selectedProjectId ? { projectId: selectedProjectId } : undefined,
  );
  const { data: billingPeriods } = useBillingPeriods();
  const createMutation = useCreateSubPayApp();

  const openBillingPeriods = (billingPeriods ?? []).filter((bp) => bp.status === "OPEN");
  const subcontracts = (commitmentsList ?? []).filter((c) => c.commitmentType === "subcontract");

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load pay applications"}
        onRetry={() => refetch()}
      />
    );
  }

  const items = data ?? [];
  const statusFilter = statusToFilter(activeStatus);

  const uniqueProjects = Array.from(
    new Map(items.map((p) => [p.projectId, p.projectName])).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const uniqueVendors = Array.from(new Set(items.map((p) => p.vendor).filter(Boolean))).sort(
    (a, b) => a.localeCompare(b),
  );

  const q = searchQuery.trim().toLowerCase();
  const filtered = items.filter((p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (!scoped && filterProject !== "__all__" && p.projectId !== filterProject) return false;
    if (filterVendor !== "__all__" && p.vendor !== filterVendor) return false;
    if (q) {
      const haystack = [
        p.vendor,
        p.projectName,
        p.commitmentNumber,
        String(p.payAppNumber),
        formatStatusLabel(p.status),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  function handleSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    switch (sortCol) {
      case "payAppNumber":
        return dir * ((a.payAppNumber ?? 0) - (b.payAppNumber ?? 0));
      case "projectName":
        return dir * (a.projectName ?? "").localeCompare(b.projectName ?? "");
      case "vendor":
        return dir * (a.vendor ?? "").localeCompare(b.vendor ?? "");
      case "commitmentNumber":
        return dir * (a.commitmentNumber ?? "").localeCompare(b.commitmentNumber ?? "");
      case "billingPeriodEnd":
        return (
          dir *
          (new Date(a.billingPeriodEnd ?? "").getTime() -
            new Date(b.billingPeriodEnd ?? "").getTime())
        );
      case "currentPaymentDue":
        return dir * ((a.currentPaymentDue ?? 0) - (b.currentPaymentDue ?? 0));
      case "createdAt":
        return (
          dir * (new Date(a.createdAt ?? "").getTime() - new Date(b.createdAt ?? "").getTime())
        );
      case "status":
        return dir * (a.status ?? "").localeCompare(b.status ?? "");
      default:
        return 0;
    }
  });

  function SortIcon({ col }: { col: SortCol }) {
    if (sortCol !== col) return null;
    return sortDir === "asc" ? (
      <ChevronUp className="inline h-3 w-3 ml-0.5 opacity-70" />
    ) : (
      <ChevronDown className="inline h-3 w-3 ml-0.5 opacity-70" />
    );
  }

  function downloadCSV() {
    const headers = [
      "App #",
      ...(scoped ? [] : ["Project"]),
      "Subcontractor",
      "Commitment",
      "Billing Period",
      "Amount Due",
      "Compliance",
      "Lien Waiver",
      "Added On",
      "Submitted By",
      "Status",
    ];
    const rows = sorted.map((p) => [
      p.payAppNumber ?? "",
      ...(scoped ? [] : [p.projectName ?? ""]),
      p.vendor ?? "",
      p.commitmentNumber ?? "",
      formatDate(p.billingPeriodEnd),
      formatCurrency(p.currentPaymentDue),
      p.complianceGatePassed ? "Yes" : "No",
      p.lienWaiverReceived ? "Yes" : "No",
      formatDate(p.createdAt),
      p.submittedByName ?? "",
      formatStatusLabel(p.status),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "subcontract-pay-apps.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleCreate() {
    if (!commitmentId) return;
    createMutation.mutate(
      { commitmentId, billingPeriodId: billingPeriodId || undefined },
      {
        onSuccess: (result) => {
          setDialogOpen(false);
          if (!projectId) setSelectedProjectId("");
          setCommitmentId("");
          setBillingPeriodId("");
          const id = (result as { data?: { id?: string } })?.data?.id;
          if (id) {
            router.navigate({ to: "/pay-applications/subcontract/$id", params: { id } });
          }
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {title ? <h1 className="text-2xl font-bold">{title}</h1> : <div />}
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (open && projectId) setSelectedProjectId(projectId);
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Pay App
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Create New Pay Application</DialogTitle>
              <DialogDescription>
                Create a new subcontract pay application for billing.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={selectedProjectId}
                  onValueChange={(v) => {
                    setSelectedProjectId(v);
                    setCommitmentId("");
                  }}
                  disabled={scoped}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {(projectsList ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subcontract</Label>
                <Select
                  value={commitmentId}
                  onValueChange={setCommitmentId}
                  disabled={!selectedProjectId || subcontracts.length === 0}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        !selectedProjectId
                          ? "Select a project first"
                          : subcontracts.length === 0
                            ? "No subcontracts on this project"
                            : "Select subcontract"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {subcontracts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.commitmentNumber ? `${c.commitmentNumber} – ` : ""}
                        {c.title ?? "Untitled"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedProjectId && subcontracts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {(commitmentsList?.length ?? 0) === 0
                      ? "This project has no commitments yet. Add a subcontract before billing."
                      : `This project has ${commitmentsList?.length} commitment${commitmentsList?.length === 1 ? "" : "s"}, but none are subcontracts. Pay applications can only be created against subcontract commitments.`}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label>Billing Period</Label>
                <Select value={billingPeriodId} onValueChange={setBillingPeriodId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Most recent open period (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    {openBillingPeriods.map((bp) => (
                      <SelectItem key={bp.id} value={bp.id}>
                        {bp.periodStart} – {bp.periodEnd}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {openBillingPeriods.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No open billing periods. An admin can create one in Configuration → Billing
                    Periods.
                  </p>
                ) : null}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!selectedProjectId || !commitmentId || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status filter buttons */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <Button
            key={s}
            variant={activeStatus === s ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by subcontractor, project, commitment…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        {scoped ? null : (
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All projects</SelectItem>
              {uniqueProjects.map(([id, name]) => (
                <SelectItem key={id} value={id}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={filterVendor} onValueChange={setFilterVendor}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All subcontractors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All subcontractors</SelectItem>
            {uniqueVendors.map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(!scoped && filterProject !== "__all__") || filterVendor !== "__all__" || searchQuery ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterProject("__all__");
              setFilterVendor("__all__");
              setSearchQuery("");
            }}
          >
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        ) : null}
        <Button variant="outline" size="sm" onClick={downloadCSV} className="ml-auto">
          <Download className="mr-1.5 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      {sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p>No subcontract pay applications found.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("payAppNumber")}
                >
                  App # <SortIcon col="payAppNumber" />
                </TableHead>
                {scoped ? null : (
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => handleSort("projectName")}
                  >
                    Project <SortIcon col="projectName" />
                  </TableHead>
                )}
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("vendor")}
                >
                  Subcontractor <SortIcon col="vendor" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("commitmentNumber")}
                >
                  Commitment <SortIcon col="commitmentNumber" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("billingPeriodEnd")}
                >
                  Billing Period <SortIcon col="billingPeriodEnd" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort("currentPaymentDue")}
                >
                  Amount Due <SortIcon col="currentPaymentDue" />
                </TableHead>
                <TableHead className="text-center">Compliance</TableHead>
                <TableHead className="text-center">Lien Waiver</TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("createdAt")}
                >
                  Added On <SortIcon col="createdAt" />
                </TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort("status")}
                >
                  Status <SortIcon col="status" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((payApp) => (
                <TableRow
                  key={payApp.id}
                  className="cursor-pointer"
                  onClick={() =>
                    router.navigate({
                      to: "/pay-applications/subcontract/$id",
                      params: { id: payApp.id },
                    })
                  }
                >
                  <TableCell className="font-mono font-medium">
                    {payApp.payAppNumber ?? "--"}
                  </TableCell>
                  {scoped ? null : (
                    <TableCell className="text-sm">{payApp.projectName || "--"}</TableCell>
                  )}
                  <TableCell>{payApp.vendor || "--"}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        router.navigate({
                          to: "/commitments/$id",
                          params: { id: payApp.commitmentId },
                        });
                      }}
                      className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 hover:underline"
                    >
                      {payApp.commitmentNumber || "--"}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(payApp.billingPeriodEnd)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(payApp.currentPaymentDue)}
                  </TableCell>
                  <TableCell className="text-center">
                    {payApp.complianceGatePassed ? (
                      <ShieldCheck className="mx-auto h-4 w-4 text-green-600" />
                    ) : (
                      <ShieldAlert className="mx-auto h-4 w-4 text-red-500" />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {payApp.lienWaiverReceived ? (
                      <ShieldCheck className="mx-auto h-4 w-4 text-green-600" />
                    ) : (
                      <span className="text-muted-foreground">--</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(payApp.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm">{payApp.submittedByName ?? "--"}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(payApp.status)}>
                      {formatStatusLabel(payApp.status)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
